import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config/api';

const getUserSession = () => {
  try {
    const raw = localStorage.getItem('tk_fashion_user_session');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

const parseJsonSafe = async (res) => {
  const text = await res.text();
  try { return text ? JSON.parse(text) : {}; } catch { return {}; }
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const STATUS_META = {
  open:     { label: 'Open',     color: '#2563eb', bg: '#dbeafe' },
  assigned: { label: 'In Progress', color: '#7c3aed', bg: '#ede9fe' },
  closed:   { label: 'Closed',   color: '#6b7280', bg: '#f3f4f6' },
};

// ─── Double tick icon ────────────────────────────────────────────────────────
const DoubleTickIcon = ({ read }) => (
  <svg
    className={`support-tick-icon ${read ? 'read' : ''}`}
    viewBox="0 0 20 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label={read ? 'Read' : 'Sent'}
  >
    {/* First tick */}
    <polyline points="1,5 4,8 9,2" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    {/* Second tick (shifted right) */}
    <polyline points="6,5 9,8 14,2" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── New Ticket Modal ─────────────────────────────────────────────────────────
function NewTicketModal({ token, onCreated, onClose }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      setError('Both subject and message are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/tickets`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ subject: subject.trim(), message: message.trim() }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Failed to create ticket');
      onCreated(data.ticket);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="support-modal-overlay" onClick={onClose}>
      <div className="support-modal" onClick={(e) => e.stopPropagation()}>
        <div className="support-modal-header">
          <h3>New Support Ticket</h3>
          <button type="button" className="support-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="support-modal-body">
          <label className="support-field-label">Subject</label>
          <input
            className="support-input"
            type="text"
            placeholder="e.g. My order hasn't arrived"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            disabled={loading}
          />
          <label className="support-field-label" style={{ marginTop: 16 }}>Message</label>
          <textarea
            className="support-textarea"
            placeholder="Describe your issue in detail…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            maxLength={2000}
            disabled={loading}
          />
          {error && <p className="support-error">{error}</p>}
          <div className="support-modal-actions">
            <button type="button" className="support-btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="support-btn-primary" disabled={loading}>
              {loading ? 'Submitting…' : 'Submit Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Chat View ────────────────────────────────────────────────────────────────
function ChatView({ ticket, token, userId, onBack, onTicketUpdate }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tickets/my/${ticket._id}`, {
        headers: authHeaders(token),
      });
      const data = await parseJsonSafe(res);
      if (res.ok) {
        setMessages(data.messages || []);
        if (data.ticket) onTicketUpdate(data.ticket);
        // Mark admin messages as read whenever we fetch
        fetch(`${API_BASE_URL}/api/tickets/${ticket._id}/read`, {
          method: 'PATCH',
          headers: authHeaders(token),
        }).catch(() => {});
      }
    } catch {
      // swallow polling errors
    } finally {
      setLoadingMsgs(false);
    }
  }, [ticket._id, token, onTicketUpdate]);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tickets/${ticket._id}/messages`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ body: draft.trim() }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Failed to send');
      setDraft('');
      fetchMessages();
    } catch {
      // ignore send errors silently
    } finally {
      setSending(false);
    }
  };

  const isClosed = ticket.status === 'closed';
  const meta = STATUS_META[ticket.status] || STATUS_META.open;

  return (
    <div className="support-chat-view">
      <div className="support-chat-header">
        <button type="button" className="support-back-btn" onClick={onBack}>
          ← Back
        </button>
        <div className="support-chat-title">
          <h3>{ticket.subject}</h3>
          <div className="support-chat-meta">
            <span className="support-status-badge" style={{ color: meta.color, background: meta.bg }}>
              {meta.label}
            </span>
            {ticket.assignedAdminName && (
              <span className="support-assigned-tag">Handled by {ticket.assignedAdminName}</span>
            )}
            <span className="support-chat-date">Opened {formatTime(ticket.createdAt)}</span>
          </div>
        </div>
      </div>

      <div className="support-messages-area">
        {loadingMsgs ? (
          <div className="support-messages-loading">Loading messages…</div>
        ) : messages.length === 0 ? (
          <div className="support-messages-empty">No messages yet.</div>
        ) : (
          messages.map((msg) => {
            const isMine = msg.senderRole === 'user';
            return (
              <div key={msg._id} className={`support-msg-row ${isMine ? 'mine' : 'theirs'}`}>
                {!isMine && (
                  <div className="support-msg-avatar">
                    <span>TK</span>
                  </div>
                )}
                <div className={`support-msg-bubble ${isMine ? 'mine' : 'theirs'}`}>
                  <p className="support-msg-body">{msg.body}</p>
                  <span className="support-msg-footer">
                    <span className="support-msg-time">{formatTime(msg.createdAt)}</span>
                    {isMine && <DoubleTickIcon read={!!msg.readAt} />}
                  </span>
                </div>
              </div>
            );
          })
        )}
        {isClosed && (
          <div className="support-closed-banner">
            🔒 This ticket has been closed. Start a new ticket if you need further help.
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {!isClosed && (
        <form className="support-input-bar" onSubmit={sendMessage}>
          <textarea
            className="support-chat-input"
            placeholder="Type a message…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={1}
            maxLength={2000}
            disabled={sending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage(e);
              }
            }}
          />
          <button type="submit" className="support-send-btn" disabled={sending || !draft.trim()}>
            {sending ? '…' : '➤'}
          </button>
        </form>
      )}
    </div>
  );
}

// ─── Ticket List Item ─────────────────────────────────────────────────────────
function TicketCard({ ticket, onClick }) {
  const meta = STATUS_META[ticket.status] || STATUS_META.open;
  return (
    <button type="button" className="support-ticket-card" onClick={() => onClick(ticket)}>
      <div className="support-ticket-card-top">
        <span className="support-ticket-subject">{ticket.subject}</span>
        <span className="support-status-badge" style={{ color: meta.color, background: meta.bg }}>
          {meta.label}
        </span>
      </div>
      <div className="support-ticket-card-bottom">
        <span className="support-ticket-date">{formatTime(ticket.lastMessageAt || ticket.createdAt)}</span>
        {ticket.assignedAdminName && (
          <span className="support-ticket-admin">Handled by {ticket.assignedAdminName}</span>
        )}
      </div>
    </button>
  );
}

// ─── Main SupportPage ─────────────────────────────────────────────────────────
export default function SupportPage({ onNotify }) {
  const session = getUserSession();
  const token = session?.token;
  const userId = session?.user?._id;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchTickets = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/tickets/my`, {
        headers: authHeaders(token),
      });
      const data = await parseJsonSafe(res);
      if (res.ok) setTickets(data.tickets || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleTicketCreated = (ticket) => {
    setShowNewModal(false);
    setTickets((prev) => [ticket, ...prev]);
    setActiveTicket(ticket);
    if (onNotify) onNotify('Support ticket created!', 'success');
  };

  const handleTicketUpdate = useCallback((updated) => {
    setTickets((prev) => prev.map((t) => t._id === updated._id ? updated : t));
    if (activeTicket?._id === updated._id) setActiveTicket(updated);
  }, [activeTicket?._id]);

  if (!token) {
    return (
      <div className="support-page">
        <div className="support-unauth">
          <div className="support-unauth-icon">💬</div>
          <h2>Support Centre</h2>
          <p>Please log in to view or create support tickets.</p>
        </div>
      </div>
    );
  }

  const filtered = filter === 'all' ? tickets : tickets.filter((t) => t.status === filter);

  if (activeTicket) {
    return (
      <div className="support-page">
        <ChatView
          ticket={activeTicket}
          token={token}
          userId={userId}
          onBack={() => { setActiveTicket(null); fetchTickets(); }}
          onTicketUpdate={handleTicketUpdate}
        />
      </div>
    );
  }

  return (
    <div className="support-page">
      <div className="support-header">
        <div className="support-header-left">
          <h2 className="support-title">Support Centre</h2>
          <p className="support-subtitle">Get help from the TK Fashion team</p>
        </div>
        <button type="button" className="support-btn-primary" onClick={() => setShowNewModal(true)}>
          + New Ticket
        </button>
      </div>

      <div className="support-filter-bar">
        {['all', 'open', 'assigned', 'closed'].map((f) => (
          <button
            key={f}
            type="button"
            className={`support-filter-btn ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : STATUS_META[f]?.label || f}
          </button>
        ))}
        <span className="support-filter-count">{filtered.length} ticket{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div className="support-loading">
          <div className="support-spinner" />
          <p>Loading tickets…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="support-empty">
          <div className="support-empty-icon">🎫</div>
          <h3>{filter === 'all' ? 'No tickets yet' : `No ${STATUS_META[filter]?.label || filter} tickets`}</h3>
          <p>Have a question or issue? Create a new ticket and we'll get back to you.</p>
          <button type="button" className="support-btn-primary" onClick={() => setShowNewModal(true)}>
            Create Your First Ticket
          </button>
        </div>
      ) : (
        <div className="support-ticket-list">
          {filtered.map((ticket) => (
            <TicketCard key={ticket._id} ticket={ticket} onClick={setActiveTicket} />
          ))}
        </div>
      )}

      {showNewModal && (
        <NewTicketModal
          token={token}
          onCreated={handleTicketCreated}
          onClose={() => setShowNewModal(false)}
        />
      )}
    </div>
  );
}
